import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../stores/useAuthStore';
import OTPInput from '../components/OTPInput';
import LMSHeader from '../components/LMSHeader';
import { useToast } from '../components/ToastContainer';

const Register = () => {
  const user = useAuthStore(state => state.user);
  const navigate = useNavigate();
  const toast = useToast();
  const { sendOTP, verifyOTP, isLoading, error } = useAuthStore();
  const [isInstructor, setIsInstructor] = useState(false);
  const location = useLocation();

  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const [step, setStep] = useState('form'); // 'form', 'otp'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    userType: 'student',
    institution: '',
    expertise: '',
  });
  const [otp, setOtp] = useState('');

  const rolePaths = {
    student: '/student/dashboard',
    instructor: '/instructor/dashboard'
  };

  useEffect(() => {
    if (user && user.role) {
      toast.info('Your are already authenticated!');
      navigate(rolePaths[user.role] || '/');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (location.state?.userType === "instructor") {
      setIsInstructor(true);
      setFormData((prev) => ({ ...prev, userType: "instructor" }));
    }
  }, [location.state]);

  useEffect(() => {
    const redirectUrl = localStorage.getItem('redirectAfterLogin');
    if (redirectUrl) {
      navigate(redirectUrl);
      localStorage.removeItem('redirectAfterLogin');
    }
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Full name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    if (isInstructor && !formData.institution) newErrors.institution = 'Institution is required';
    if (isInstructor && !formData.expertise) newErrors.expertise = 'Area of expertise is required';
    return newErrors;
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    const result = await sendOTP(formData.email);
    if (result.success) {
      setStep('otp');
    }
  };

  const handleVerifyOTP = async (otpValue) => {

    const result = await verifyOTP(
      formData.email,
      otpValue,
      formData.name,
      formData.password,
      formData.userType,
      formData.institution,
      formData.expertise,
    );

    if (result.success) {
      const redirectUrl = localStorage.getItem('redirectAfterLogin');

      if (typeof window !== 'undefined' && window.Tawk_API) {
        window.Tawk_API.setAttributes({
          name: result?.user?.name || result?.user?.email.split('@')[0],
          email: result?.user?.email,
        });
      }

      toast.success('Registered successfully, we are logging you in now');

      if (redirectUrl) {
        navigate(redirectUrl);
        localStorage.removeItem('redirectAfterLogin');
      } else {
        navigate(rolePaths[result.user.role] || '/');
      }
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex flex-col relative">

      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url('/images/login-bg-two.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
      </div>

      <LMSHeader />
      <main className="flex-1 flex items-center justify-center p-4 relative z-10">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-neutral-200 dark:border-neutral-700 p-8 backdrop-blur-sm">

            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
                {isInstructor ? 'Become an instructor' : 'Create Your Account'}
              </h1>
              <p className="text-neutral-600 dark:text-neutral-400 mt-2">
                {isInstructor
                  ? 'Share your expertise with the world. BoleumLearn empowers you with intuitive tools to design, launch, and teach.'
                  : 'Join our learning community today.'}
              </p>
            </div>

            <div className="flex justify-center mb-8">
              <div className="inline-flex rounded-lg p-1 bg-neutral-100 dark:bg-neutral-700">
                {/* Student Button */}
                <button
                  type="button"
                  value="student"
                  onClick={(e) => {
                    setIsInstructor(false);
                    setFormData({ ...formData, userType: e.target.value });
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${!isInstructor
                    ? "bg-white dark:bg-neutral-800 text-primary-600 dark:text-primary-400 shadow-sm"
                    : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
                    }`}
                >
                  Student
                </button>

                {/* Instructor Button */}
                <button
                  type="button"
                  value="instructor"
                  onClick={(e) => {
                    setIsInstructor(true);
                    setFormData({ ...formData, userType: e.target.value });
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${isInstructor
                    ? "bg-white dark:bg-neutral-800 text-primary-600 dark:text-primary-400 shadow-sm"
                    : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
                    }`}
                >
                  Instructor
                </button>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-3 bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 rounded-lg text-sm">
                {error}
              </div>
            )}

            {step === 'form' ? (
              <form onSubmit={handleSendOTP} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="John Doe"
                  />
                  {errors.name && <p className="mt-1 text-sm text-danger-500">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="your.email@email.com"
                  />
                  {errors.email && <p className="mt-1 text-sm text-danger-500">{errors.email}</p>}
                </div>

                <div className="relative w-full">
                  <label htmlFor="password" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Password
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="••••••••"
                  />
                  {errors.password && <p className="mt-1 text-sm text-danger-500">{errors.password}</p>}
                  <span
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-2/2 cursor-pointer select-none text-xl"
                  >
                    {showPassword ? '🛡️' : '🔐'}
                  </span>
                </div>

                <div className="relative w-full">
                  <label htmlFor="password" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Confirm Password
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="cpassword"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="••••••••"
                  />
                  {errors.confirmPassword && <p className="mt-1 text-sm text-danger-500">{errors.confirmPassword}</p>}

                  <span
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-2/2 cursor-pointer select-none text-xl"
                  >
                    {showPassword ? '🛡️' : '🔐'}
                  </span>
                </div>

                {/* Instructor Fields */}
                {isInstructor && (
                  <>
                    <div>
                      <label htmlFor="institution" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                        Institution / Company
                      </label>
                      <input
                        id="institution"
                        name="institution"
                        type="text"
                        value={formData.institution}
                        onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                        className={`w-full px-4 py-3 border rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 ${errors.institution
                          ? 'border-danger-500 focus:ring-danger-500'
                          : 'border-neutral-300 dark:border-neutral-600 focus:ring-primary-500'
                          }`}
                        placeholder="Your University or Company"
                      />
                      {errors.institution && <p className="mt-1 text-sm text-danger-500">{errors.institution}</p>}
                    </div>

                    <div>
                      <label htmlFor="expertise" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                        Area of Expertise
                      </label>
                      <input
                        id="expertise"
                        name="expertise"
                        type="text"
                        value={formData.expertise}
                        onChange={(e) => setFormData({ ...formData, expertise: e.target.value })}
                        className={`w-full px-4 py-3 border rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 ${errors.expertise
                          ? 'border-danger-500 focus:ring-danger-500'
                          : 'border-neutral-300 dark:border-neutral-600 focus:ring-primary-500'
                          }`}
                        placeholder="e.g., Web Development, Data Science"
                      />
                      {errors.expertise && <p className="mt-1 text-sm text-danger-500">{errors.expertise}</p>}
                    </div>
                  </>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 transition-all duration-200"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending OTP...
                    </span>
                  ) : (
                    'Send OTP'
                  )}
                </button>
              </form>
            ) : (
              <div className="space-y-6 relative min-h-[200px] flex flex-col items-center justify-center">
                {isLoading ? (
                  <div className='absolute inset-0 flex justify-center items-center'>
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                  </div>
                ) : (
                  <>
                    <div className="text-center">
                      <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
                        Enter OTP
                      </h2>
                      <p className="text-neutral-600 dark:text-neutral-400">
                        We've sent a 6-digit code to {formData.email}
                      </p>
                    </div>

                    <div className="flex justify-center">
                      <OTPInput
                        length={6}
                        onChange={setOtp}
                        onComplete={handleVerifyOTP}
                      />
                    </div>

                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => setStep('form')}
                        className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        Back to registration
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-neutral-200 dark:border-neutral-700 text-center">
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                Already have an account?{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
                >
                  Login
                </button>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Register;